import { Link, useRouterState } from '@tanstack/react-router';
import { FC, useState, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@new/lib';
import { Can } from '@new/casl';
import type { SidebarConfigItem } from './sidebarConfig';
import { ICON_SIZES, ICON_STROKE_WIDTH, SUBMENU_MAX_HEIGHT } from './constants';

interface SidebarItemProps {
  item: SidebarConfigItem;
  collapsed: boolean;
  onSidebarToggle: () => void;
}

export const SidebarItem: FC<SidebarItemProps> = ({ item, collapsed, onSidebarToggle }) => {
  const pathname = useRouterState().location.pathname;

  const hasChildren = Boolean(item.children?.length);

  const hasActiveChild = useMemo(
    () =>
      item.children?.some(
        (child) => pathname === child.to || pathname.startsWith(child.to + '/')
      ) ?? false,
    [item.children, pathname]
  );

  const isActive = useMemo(
    () => item.to !== '' && (pathname === item.to || pathname.startsWith(item.to + '/')),
    [item.to, pathname]
  );

  const [open, setOpen] = useState(hasActiveChild);

  const showSubmenu = hasChildren && !collapsed;
  const isCurrent = isActive || hasActiveChild;

  const onParentClick = () => {
    setOpen((prev) => !prev);

    if (hasChildren && collapsed) {
      onSidebarToggle();
    }
  };

  const IconComponent = item.icon;

  const ItemContent = (
    <div
      className={cn(
        'flex items-center gap-2 transition-[color]',
        hasChildren && 'cursor-pointer',
        isCurrent
          ? 'text-(--primary-O1000) group-hover:text-(--neutral-black-NB600)'
          : 'text-(--neutral-black-NB600)'
      )}
    >
      <span className="flex h-8 w-8 justify-center items-center transition-[color]">
        <IconComponent strokeWidth={ICON_STROKE_WIDTH} className="w-4.25 h-4.25" />
      </span>

      <span
        className={cn(
          'l4 whitespace-nowrap transition-all font-montserrat!',
          collapsed ? 'hidden' : 'inline',
          isCurrent ? 'text-(--primary-O1000)! medium' : 'text-(--neutral-black-NB600)! regular'
        )}
      >
        {item.label}
      </span>

      {hasChildren && !collapsed && (
        <ChevronDown
          size={ICON_SIZES.CHEVRON}
          className={cn('transition-transform', open && 'rotate-180')}
        />
      )}
    </div>
  );

  const containerClasses = cn(
    'group flex items-center self-stretch rounded-lg',
    'transition-[background-color,color] duration-180 ease-in-out no-underline!',
    isCurrent
      ? 'bg-(--primary-O200)! text-(--primary-O1000)!'
      : 'bg-transparent text-(--neutral-black-NB600)',
    'hover:bg-(--neutral-black-NB100)! hover:text-(--neutral-black-NB600)!',
    collapsed ? 'justify-center w-fit' : 'justify-start w-full'
  );

  return (
    <>
      {hasChildren ? (
        <div
          role="button"
          tabIndex={0}
          aria-expanded={open}
          aria-label={`${item.label} menu`}
          onClick={onParentClick}
          className={containerClasses}
        >
          {ItemContent}
        </div>
      ) : (
        <Link to={item.to} className={containerClasses}>
          {ItemContent}
        </Link>
      )}

      {showSubmenu && (
        <div
          className={cn(
            'overflow-hidden transition-all duration-300 ease shrink-0',
            open ? `max-h-[${SUBMENU_MAX_HEIGHT}px] opacity-100` : 'max-h-0 opacity-0 mt-0',
            collapsed ? 'ml-0' : 'ml-4 lg:ml-6'
          )}
        >
          <div className="flex flex-col gap-2 lg:gap-3">
            {item.children?.map((child) => (
              <Can key={child.key} I={child.action} a={child.subject}>
                <SidebarItem item={child} collapsed={false} onSidebarToggle={onSidebarToggle} />
              </Can>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
